import { useEffect, useState } from 'react';
import {
  getCurrentChannel,
  switchToStaging,
  resetToProduction,
  isNativeOta,
  OTA_CHANNELS,
} from '../utils/otaChannel';
// TEMPORARY — see the removal checklist at the bottom of HeaderDebugReadout.jsx.
import { isHeaderDebugEnabled, setHeaderDebugEnabled } from '../components/HeaderDebugReadout';
// TEMPORARY — see the removal checklist at the bottom of OtaUpdateReadout.jsx.
import OtaUpdateReadout from '../components/OtaUpdateReadout';

// AdminOta — STAFF-ONLY OTA channel control. Registered at /admin/ota behind
// <AdminOnly> (role === 'admin'), so it is unreachable by real borrowers.
//
// Purpose: let staff put THEIR OWN device on the 'staging' channel to run the
// MANDATORY WKWebView smoke-test (joint-intent capture, ReadyCheck, e-sign,
// disclosures) on a merged bundle BEFORE it is promoted to production via the
// gated ota-publish workflow. Switching is device-local — it changes nothing
// for any other user.
export default function AdminOta() {
  const native = isNativeOta();
  const [channel, setChannel] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [hdrDebug, setHdrDebug] = useState(isHeaderDebugEnabled);

  useEffect(() => {
    if (!native) return;
    getCurrentChannel().then(setChannel).catch((e) => setErr(String(e?.message || e)));
  }, [native]);

  async function run(fn) {
    setBusy(true);
    setErr('');
    try {
      setChannel(await fn());
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setBusy(false);
    }
  }

  const onStaging = channel === OTA_CHANNELS.STAGING;

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: 24, fontFamily: 'system-ui, -apple-system, sans-serif' }}>
      <h1 style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 6 }}>OTA Channel (staff)</h1>
      <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 20, lineHeight: 1.6 }}>
        Put this device on <strong>staging</strong> to smoke-test a merged bundle before it is promoted to
        production. Device-local — this changes nothing for borrowers or other staff.
      </p>

      {!native && (
        <div style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '12px 16px', fontSize: 13, color: '#9a3412', marginBottom: 20 }}>
          OTA channels only exist inside the native app. This browser/PWA session always runs the bundle it was served.
        </div>
      )}

      <div style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px', marginBottom: 20 }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: 4 }}>Current channel</div>
        <div style={{ fontSize: 20, fontWeight: 700, color: onStaging ? '#b45309' : '#166534' }}>
          {channel || (native ? '—' : 'n/a')}
        </div>
      </div>

      {err && (
        <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 14px', fontSize: 13, color: '#991b1b', marginBottom: 16 }}>
          {err}
        </div>
      )}

      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
        <button
          onClick={() => run(switchToStaging)}
          disabled={!native || busy || onStaging}
          style={btn('#b45309', !native || busy || onStaging)}
        >
          Switch this device to staging
        </button>
        <button
          onClick={() => run(resetToProduction)}
          disabled={!native || busy || channel === OTA_CHANNELS.PRODUCTION}
          style={btn('#166534', !native || busy || channel === OTA_CHANNELS.PRODUCTION)}
        >
          Reset to production
        </button>
      </div>

      {/* ── TEMPORARY: update-check readout ───────────────────────────────
          The instrument for the min_update_version rehearsal. Lives here
          because getLatest() returns data and there is no console on the
          device to read it in — no Mac for Safari Web Inspector, and
          chrome://inspect cannot attach to a store build. Read its header
          block before quoting any number out of it: the production/staging
          pair is the VERSION-PIN finding, not the rehearsal's answer.
          Remove with the checklist at the bottom of OtaUpdateReadout.jsx. */}
      <OtaUpdateReadout />

      {/* ── TEMPORARY: header-band diagnostics ────────────────────────────
          Lives here because there is NO ADDRESS BAR in the native app, so
          ?hdrdebug=1 cannot be typed on the device the measurement is for.
          This is the reachable switch. Remove with the readout — checklist at
          the bottom of HeaderDebugReadout.jsx. */}
      <div style={{ marginTop: 24, background: '#fff', border: '1px solid #e5e7eb', borderRadius: 10, padding: '16px 20px' }}>
        <div style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.08em', color: '#6b7280', marginBottom: 6 }}>
          Header debug readout (temporary)
        </div>
        <p style={{ fontSize: 12.5, color: '#6b7280', lineHeight: 1.6, margin: '0 0 12px' }}>
          Draws a live numeric overlay in the bottom-left corner of <strong>/apply</strong> — safe-area inset,
          header rect and padding, scroll position, visual-viewport — updating every frame, with the
          min…max range seen since the page loaded. Turn it on, scroll <strong>/apply</strong> while
          screen-recording, then turn it off. Reload /apply after toggling.
        </p>
        <button
          onClick={() => { const next = !hdrDebug; setHeaderDebugEnabled(next); setHdrDebug(next); }}
          style={btn(hdrDebug ? '#b45309' : '#374151', false)}
        >
          {hdrDebug ? 'Turn readout OFF' : 'Turn readout ON'}
        </button>
      </div>

      <div style={{ marginTop: 24, fontSize: 12.5, color: '#6b7280', lineHeight: 1.7 }}>
        <strong style={{ color: '#111827' }}>Required smoke-test on staging before promotion:</strong>
        <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
          <li>Joint-intent capture (affirmation records; IP/UA captured correctly)</li>
          <li>ReadyCheck / credit-pull gating</li>
          <li>E-sign flow (signer token, canvas signature)</li>
          <li>Disclosures + adverse-action text render correctly in WKWebView</li>
        </ul>
      </div>
    </div>
  );
}

function btn(bg, disabled) {
  return {
    background: disabled ? '#e5e7eb' : bg,
    color: disabled ? '#9ca3af' : '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '10px 18px',
    fontSize: 13,
    fontWeight: 600,
    cursor: disabled ? 'not-allowed' : 'pointer',
  };
}
